import { z } from 'zod';
import { CategorySchema, ImageOptimizationSchema } from './common.js';
import { TagSchema } from './tags.js';

/**
 * Markets endpoint Zod schemas for Polymarket Gamma API
 */

export const ListMarketsParamsSchema = z.object({
  limit: z.number().optional(),
  offset: z.number().optional(),
  order: z.string().optional(),
  ascending: z.boolean().optional(),
  id: z.array(z.number()).optional(),
  slug: z.array(z.string()).optional(),
  clob_token_ids: z.array(z.string()).optional(),
  condition_ids: z.array(z.string()).optional(),
  market_maker_address: z.array(z.string()).optional(),
  liquidity_num_min: z.number().optional(),
  liquidity_num_max: z.number().optional(),
  volume_num_min: z.number().optional(),
  volume_num_max: z.number().optional(),
  start_date_min: z.string().optional(),
  start_date_max: z.string().optional(),
  end_date_min: z.string().optional(),
  end_date_max: z.string().optional(),
  tag_id: z.number().optional(),
  related_tags: z.boolean().optional(),
  cyom: z.boolean().optional(),
  uma_resolution_status: z.string().optional(),
  game_id: z.string().optional(),
  sports_market_types: z.array(z.string()).optional(),
  rewards_min_size: z.number().optional(),
  question_ids: z.array(z.string()).optional(),
  include_tag: z.boolean().optional(),
  closed: z.boolean().optional(),
});

export const GetMarketByIdParamsSchema = z.object({
  id: z.number(),
  include_tag: z.boolean().optional(),
});

export const GetMarketBySlugParamsSchema = z.object({
  slug: z.string(),
  include_tag: z.boolean().optional(),
});

export const GetMarketTagsParamsSchema = z.object({
  id: z.number(),
});

export const MarketsInformationBodySchema = z.object({
  id: z.array(z.number()).optional(),
  slug: z.array(z.string()).optional(),
  closed: z.boolean().nullable().optional(),
  clobTokenIds: z.array(z.string()).optional(),
  conditionIds: z.array(z.string()).optional(),
  marketMakerAddress: z.array(z.string()).optional(),
  liquidityNumMin: z.number().nullable().optional(),
  liquidityNumMax: z.number().nullable().optional(),
  volumeNumMin: z.number().nullable().optional(),
  volumeNumMax: z.number().nullable().optional(),
  startDateMin: z.string().nullable().optional(),
  startDateMax: z.string().nullable().optional(),
  endDateMin: z.string().nullable().optional(),
  endDateMax: z.string().nullable().optional(),
  relatedTags: z.boolean().nullable().optional(),
  tagId: z.number().nullable().optional(),
  cyom: z.boolean().nullable().optional(),
  umaResolutionStatus: z.string().nullable().optional(),
  gameId: z.string().nullable().optional(),
  sportsMarketTypes: z.array(z.string()).optional(),
  rewardsMinSize: z.number().nullable().optional(),
  questionIds: z.array(z.string()).optional(),
  includeTags: z.boolean().nullable().optional(),
});

// Forward declarations for circular dependencies
const EventSchema = z.unknown();

export const MarketSchema = z
  .object({
    id: z.string(),
    question: z.string().nullable().optional(),
    conditionId: z.string(),
    slug: z.string().nullable().optional(),
    twitterCardImage: z.string().nullable().optional(),
    resolutionSource: z.string().nullable().optional(),
    endDate: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    ammType: z.string().nullable().optional(),
    liquidity: z.string().nullable().optional(),
    sponsorName: z.string().nullable().optional(),
    sponsorImage: z.string().nullable().optional(),
    startDate: z.string().nullable().optional(),
    xAxisValue: z.string().nullable().optional(),
    yAxisValue: z.string().nullable().optional(),
    denominationToken: z.string().nullable().optional(),
    fee: z.string().nullable().optional(),
    image: z.string().nullable().optional(),
    icon: z.string().nullable().optional(),
    lowerBound: z.string().nullable().optional(),
    upperBound: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    outcomes: z.string().nullable().optional(),
    outcomePrices: z.string().nullable().optional(),
    volume: z.string().nullable().optional(),
    active: z.boolean().nullable().optional(),
    marketType: z.string().nullable().optional(),
    formatType: z.string().nullable().optional(),
    lowerBoundDate: z.string().nullable().optional(),
    upperBoundDate: z.string().nullable().optional(),
    closed: z.boolean().nullable().optional(),
    marketMakerAddress: z.string(),
    createdBy: z.number().nullable().optional(),
    updatedBy: z.number().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
    closedTime: z.string().nullable().optional(),
    wideFormat: z.boolean().nullable().optional(),
    new: z.boolean().nullable().optional(),
    mailchimpTag: z.string().nullable().optional(),
    featured: z.boolean().nullable().optional(),
    archived: z.boolean().nullable().optional(),
    resolvedBy: z.string().nullable().optional(),
    restricted: z.boolean().nullable().optional(),
    marketGroup: z.number().nullable().optional(),
    groupItemTitle: z.string().nullable().optional(),
    groupItemThreshold: z.string().nullable().optional(),
    questionID: z.string().nullable().optional(),
    umaEndDate: z.string().nullable().optional(),
    enableOrderBook: z.boolean().nullable().optional(),
    orderPriceMinTickSize: z.number().nullable().optional(),
    orderMinSize: z.number().nullable().optional(),
    umaResolutionStatus: z.string().nullable().optional(),
    curationOrder: z.number().nullable().optional(),
    volumeNum: z.number().nullable().optional(),
    liquidityNum: z.number().nullable().optional(),
    endDateIso: z.string().nullable().optional(),
    startDateIso: z.string().nullable().optional(),
    umaEndDateIso: z.string().nullable().optional(),
    hasReviewedDates: z.boolean().nullable().optional(),
    readyForCron: z.boolean().nullable().optional(),
    commentsEnabled: z.boolean().nullable().optional(),
    volume24hr: z.number().nullable().optional(),
    volume1wk: z.number().nullable().optional(),
    volume1mo: z.number().nullable().optional(),
    volume1yr: z.number().nullable().optional(),
    gameStartTime: z.string().nullable().optional(),
    secondsDelay: z.number().nullable().optional(),
    clobTokenIds: z.string().nullable().optional(),
    disqusThread: z.string().nullable().optional(),
    shortOutcomes: z.string().nullable().optional(),
    teamAID: z.string().nullable().optional(),
    teamBID: z.string().nullable().optional(),
    umaBond: z.string().nullable().optional(),
    umaReward: z.string().nullable().optional(),
    fpmmLive: z.boolean().nullable().optional(),
    volume24hrAmm: z.number().nullable().optional(),
    volume1wkAmm: z.number().nullable().optional(),
    volume1moAmm: z.number().nullable().optional(),
    volume1yrAmm: z.number().nullable().optional(),
    volume24hrClob: z.number().nullable().optional(),
    volume1wkClob: z.number().nullable().optional(),
    volume1moClob: z.number().nullable().optional(),
    volume1yrClob: z.number().nullable().optional(),
    volumeAmm: z.number().nullable().optional(),
    volumeClob: z.number().nullable().optional(),
    liquidityAmm: z.number().nullable().optional(),
    liquidityClob: z.number().nullable().optional(),
    makerBaseFee: z.number().nullable().optional(),
    takerBaseFee: z.number().nullable().optional(),
    customLiveness: z.number().nullable().optional(),
    acceptingOrders: z.boolean().nullable().optional(),
    notificationsEnabled: z.boolean().nullable().optional(),
    score: z.number().nullable().optional(),
    imageOptimized: ImageOptimizationSchema.optional(),
    iconOptimized: ImageOptimizationSchema.optional(),
    events: z.array(EventSchema).optional(),
    categories: z.array(CategorySchema).optional(),
    tags: z.array(TagSchema).optional(),
    creator: z.string().nullable().optional(),
    ready: z.boolean().nullable().optional(),
    funded: z.boolean().nullable().optional(),
    pastSlugs: z.string().nullable().optional(),
    readyTimestamp: z.string().nullable().optional(),
    fundedTimestamp: z.string().nullable().optional(),
    acceptingOrdersTimestamp: z.string().nullable().optional(),
    competitive: z.number().nullable().optional(),
    rewardsMinSize: z.number().nullable().optional(),
    rewardsMaxSpread: z.number().nullable().optional(),
    spread: z.number().nullable().optional(),
    automaticallyResolved: z.boolean().nullable().optional(),
    oneDayPriceChange: z.number().nullable().optional(),
    oneHourPriceChange: z.number().nullable().optional(),
    oneWeekPriceChange: z.number().nullable().optional(),
    oneMonthPriceChange: z.number().nullable().optional(),
    oneYearPriceChange: z.number().nullable().optional(),
    lastTradePrice: z.number().nullable().optional(),
    bestBid: z.number().nullable().optional(),
    bestAsk: z.number().nullable().optional(),
    automaticallyActive: z.boolean().nullable().optional(),
    clearBookOnStart: z.boolean().nullable().optional(),
    chartColor: z.string().nullable().optional(),
    seriesColor: z.string().nullable().optional(),
    showGmpSeries: z.boolean().nullable().optional(),
    showGmpOutcome: z.boolean().nullable().optional(),
    manualActivation: z.boolean().nullable().optional(),
    negRiskOther: z.boolean().nullable().optional(),
    gameId: z.string().nullable().optional(),
    groupItemRange: z.string().nullable().optional(),
    sportsMarketType: z.string().nullable().optional(),
    line: z.number().nullable().optional(),
    umaResolutionStatuses: z.string().nullable().optional(),
    pendingDeployment: z.boolean().nullable().optional(),
    deploying: z.boolean().nullable().optional(),
    deployingTimestamp: z.string().nullable().optional(),
    scheduledDeploymentTimestamp: z.string().nullable().optional(),
    rfqEnabled: z.boolean().nullable().optional(),
    eventStartTime: z.string().nullable().optional(),
  })
  .passthrough(); // Allow additional properties for index signature
